import { useState } from "react";
import { Link } from "react-router-dom";
import AnimeCard from "@/components/feature/AnimeCard";
import { animeList, CATEGORIES } from "@/mocks/anime";

function SectionHead({
  eyebrow,
  title,
  to,
  linkText = "查看全部",
}: {
  eyebrow: string;
  title: string;
  to?: string;
  linkText?: string;
}) {
  return (
    <div className="mb-6 flex items-end justify-between">
      <div>
        <p className="mb-1 text-xs tracking-wide text-primary-400">{eyebrow}</p>
        <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">{title}</h2>
      </div>
      {to && (
        <Link
          to={to}
          className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-300"
        >
          {linkText}
          <i className="ri-arrow-right-line" />
        </Link>
      )}
    </div>
  );
}

/* 番剧类型分类 */
export function CategorySection() {
  return (
    <section className="mx-auto max-w-7xl px-4 md:px-6 py-14">
      <SectionHead eyebrow="全类型番剧库" title="按类型找番，一站看全" to="/library" />
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {CATEGORIES.filter((c) => c.key !== "all").map((c) => (
          <Link
            key={c.key}
            to={`/library?cat=${encodeURIComponent(c.key)}`}
            className="group relative overflow-hidden rounded-xl border border-background-200/60 bg-background-100 p-5 transition-transform duration-300 hover:-translate-y-1"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500/15 text-primary-400 transition-colors group-hover:bg-primary-500 group-hover:text-background-950">
              <i className={`${c.icon} text-xl`} />
            </span>
            <h3 className="mt-4 font-heading text-lg text-foreground-950">{c.name}</h3>
            <p className="mt-1 text-xs text-foreground-600">{c.desc}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* 热门番剧 */
export function HotSection() {
  const hot = [...animeList].sort((a, b) => b.rating - a.rating).slice(0, 8);
  return (
    <section
      id="hot"
      className="bg-gradient-to-b from-background-50 to-background-100/60 py-14"
    >
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHead eyebrow="编辑精选" title="热门番剧 TOP 榜" to="/library" />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {hot.map((a, i) => (
            <div key={a.id} className="relative">
              <span className="absolute -left-1 -top-1 z-10 font-heading text-3xl text-accent-400 drop-shadow">
                {i + 1}
              </span>
              <AnimeCard anime={a} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* 横向滚动行 */
export function ScrollRow({
  eyebrow,
  title,
  cat,
  to,
}: {
  eyebrow: string;
  title: string;
  cat: string;
  to: string;
}) {
  const items = animeList.filter((a) => a.category === cat);
  return (
    <section className="mx-auto max-w-7xl px-4 md:px-6 py-14">
      <SectionHead eyebrow={eyebrow} title={title} to={to} />
      <div className="no-scrollbar -mx-4 flex gap-4 overflow-x-auto px-4 pb-2 md:-mx-6 md:px-6">
        {items.map((a) => (
          <div key={a.id} className="w-40 shrink-0 sm:w-44 md:w-48">
            <AnimeCard anime={a} />
          </div>
        ))}
      </div>
    </section>
  );
}

/* 榜单列表 */
export function RankingSection() {
  const top = [...animeList].sort((a, b) => b.views.length - a.views.length).slice(0, 5);
  return (
    <section className="bg-background-100/60 py-14">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHead eyebrow="大家都在看" title="热播播放榜" to="/library" />
        <div className="grid gap-4 lg:grid-cols-2">
          {top.map((a, i) => (
            <Link
              key={a.id}
              to={`/detail/${a.id}`}
              className="group flex items-center gap-4 rounded-xl border border-background-200/60 bg-background-100 p-3 transition-transform duration-300 hover:-translate-y-0.5"
            >
              <div className="w-16 shrink-0 overflow-hidden rounded-lg">
                <img
                  src={a.poster}
                  alt={`${a.title} 缩略图`}
                  title={`${a.title} 播放排行`}
                  className="aspect-[2/3] w-full object-cover object-top"
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-foreground-600">
                  <span className="font-heading text-accent-400">#{i + 1}</span> ·{" "}
                  {a.category}
                </p>
                <h3 className="truncate font-medium text-foreground-950 group-hover:text-primary-300">
                  {a.title}
                </h3>
                <div className="mt-1 flex items-center gap-3 text-xs text-foreground-600">
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-eye-line" />
                    {a.views}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-star-fill text-accent-400" />
                    {a.rating}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* FAQ */
export function FaqSection() {
  const faqs = [
    {
      q: "樱花看番番剧库收录了哪些类型的番剧？",
      a: "樱花看番番剧库全收录，涵盖动画番剧、剧场版、OVA特别篇、泡面番以及国漫全类型，新番与经典同步更新。",
    },
    {
      q: "剧场版和OVA在哪里观看？",
      a: "进入番剧库后，可通过顶部类型筛选或搜索直接找到剧场版与OVA专区，点开即可在线观看，无需等待。",
    },
    {
      q: "泡面番单集时长是多少？",
      a: "泡面番单集约3-5分钟，适合碎片时间观看，等泡面的工夫刚好可以看完一集，轻松又解压。",
    },
    {
      q: "新番多久更新一次？",
      a: "热门连载新番与剧场版同步更新，国漫与动画番剧每周持续更新，建议开启收藏方便第一时间追更。",
    },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section className="mx-auto max-w-7xl px-4 md:px-6 py-14">
      <SectionHead eyebrow="常见问题" title="关于看番，你想知道的" />
      <div className="space-y-3">
        {faqs.map((f, i) => (
          <div
            key={f.q}
            className="overflow-hidden rounded-xl border border-background-200/60 bg-background-100"
          >
            <button
              onClick={() => setOpen(open === i ? -1 : i)}
              className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
            >
              <span className="flex items-center gap-3 font-medium text-foreground-950">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent-500/20 text-xs text-accent-300">
                  {i + 1}
                </span>
                {f.q}
              </span>
              <i
                className={`ri-add-line text-foreground-500 transition-transform duration-300 ${
                  open === i ? "rotate-45" : ""
                }`}
              />
            </button>
            <div
              className={`grid transition-all duration-300 ${
                open === i ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
              }`}
            >
              <div className="overflow-hidden">
                <p className="px-5 pb-5 pl-14 text-sm leading-relaxed text-foreground-600">
                  {f.a}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}